'use client';

import CustomToast from '@/components/custom-toast';
import { configKeepDataInfiniy } from '@/config/useQuery';
import { genProccessStaffInfo, nameof } from '@/lib/utils';
import { createZustandShallowStore } from '@/lib/zustand';
import { AuthenticateInfo, EmployeeAuthInfo } from '@/server/api/v1/auth/types';
import { EmployeeSearchAdvanceResponse } from '@/server/api/v1/employee/type';
import { LogicalOperator, OperatorType } from '@/server/base/base-trpc-service';
import { api } from '@/trpc/client';
import { EmployeeExpand } from '@/types/enum';
import { SetStoreState } from '@/types/use';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef } from 'react';

interface EmployeeInfoStoreProps {
    /**Khởi tạo xong thông tin nhân viên */
    isInitedEmpInfo: boolean;

    current: EmployeeAuthInfo | undefined;
    changeCurrentEmpInfo: (data: EmployeeAuthInfo | undefined) => void;
    employeeInfos: EmployeeAuthInfo[];
    setEmployeeInfos: SetStoreState<EmployeeAuthInfo[]>;
}

const { sonnerV2 } = CustomToast;

function mapToEmpAuthInfo(
    data: EmployeeSearchAdvanceResponse
): EmployeeAuthInfo {
    return {
        ID: data.ID,
        IsDisabled: data.IsDisabled,
        IsFieldManager: data.IsFieldManager,
        Ordinal: data.Ordinal,
        PositionCode: data.PositionCode,

        UnitCode: data.UnitCode,
        DepartmentCode: data.DepartmentCode,
        UserId: data.UserId,
        IsInUse: data.IsInUse,
        Type: data.Type,

        PositionId: data?.Position?.ID ?? data.PositionId,
        DepartmentId: data.Department?.ID ?? data.DepartmentId,
        UnitId: data.Unit?.ID ?? data.UnitId,
        EmployeeFullName: genProccessStaffInfo(data, true),
    };
}

/**Store lưu trữ thông tin liên quan đến nhân viên */
export const useEmployeeInfoStore =
    createZustandShallowStore<EmployeeInfoStoreProps>((set, get) => {
        return {
            isInitedEmpInfo: false,
            current: undefined,
            employeeInfos: [],
            changeCurrentEmpInfo(data) {
                set({ current: data });
            },
            setEmployeeInfos(prevState) {
                set((st) => {
                    const obj = {
                        employeeInfos:
                            typeof prevState === 'function'
                                ? prevState(st.employeeInfos)
                                : prevState,
                    };
                    return {
                        ...obj,
                        current: st.current ?? obj.employeeInfos?.[0], // DEFAULT
                        isInitedEmpInfo: true,
                    };
                });
            },
        };
    });

/**Hook gôm data employeeInfo từ session */
export const useEmployeeResolver = (authData: AuthenticateInfo | null) => {
    const apiUtils = api.useUtils();
    const transDef = useTranslations('System');

    /**
     * Fix tạm vấn đề session call lại mỗi khi window mount
     */
    const _isDone = useRef<boolean>(false);
    const setEmpInfos = useEmployeeInfoStore((x) => x.setEmployeeInfos);
    const currentEmpInfo = useEmployeeInfoStore((x) => x.current);

    /**
     * Lấy các thông tin extend của nhân viên như chức vụ, phòng ban,...
     *  */
    const onFetchExtendInfos = useCallback(
        async (empData?: EmployeeAuthInfo[]) => {
            if (empData && empData.length > 0) {
                const empIds: string[] = empData.map((x) => x.ID);

                const result = await apiUtils.employee.SearchEMPAdvance.fetch(
                    {
                        filters: [
                            {
                                key: nameof<EmployeeSearchAdvanceResponse>(
                                    'ID'
                                ),
                                values: empIds,
                                logicalOperator: LogicalOperator.And,
                                operatorType: OperatorType.In,
                            },
                        ],
                        countMaxTo: empIds.length,
                        limit: empIds.length,
                        expands: [
                            EmployeeExpand.Department,
                            EmployeeExpand.Position,
                            EmployeeExpand.Unit,
                        ],
                        isIncludeSelf: true,
                    },
                    { ...configKeepDataInfiniy }
                );

                if (result.isSuccessStatus) {
                    setEmpInfos(result.data?.Items.map(mapToEmpAuthInfo) ?? []);
                } else {
                    setEmpInfos([]);
                    sonnerV2({
                        type: 'error',
                        title: 'Đã có lỗi khi khi lấy thông tin nhân viên',
                        desc:
                            result.errors?.join(',') ??
                            transDef('System_AnErrorHasOccurred'),
                    });
                }
            } else {
                setEmpInfos([]);
            }
        },
        [apiUtils.employee.SearchEMPAdvance, setEmpInfos, transDef]
    );

    useEffect(() => {
        if (!_isDone.current) {
            onFetchExtendInfos(authData?.ExtraInfo?.EmployeeInfos);
            _isDone.current = true;
        }
    }, [authData, onFetchExtendInfos, setEmpInfos]);

    return currentEmpInfo;
};
